<template>
  <!--
    UpgradeOverlay — shell de diálogo genérico (scrim + panel con scroll propio, foco,
    Esc, teleport, lock de scroll del body) usado como CAPA sobre la app en vez de como
    página. Nació para el upsell a Cohort, pero es agnóstico del contenido: quien lo usa
    pasa el suyo por el slot por defecto (`UpgradeContent` para upsell,
    `RenewalContent` para la pantalla de "tu plan venció" — spec:
    docs/subscription-expiry-fe-contract.md).

    Por qué existe como capa y no como página: los cinco items con candado del sidebar
    navegaban a `/upgrade`, y esa vista monta su PROPIO nav — o sea que clickear "Monitor"
    te expulsaba de la aplicación: desaparecía el sidebar, perdías la página en la que
    estabas, y el "Back" te devolvía al Home y no a donde estabas. Acá el contexto se
    conserva: la app queda atrás, atenuada, y al cerrar sigues exactamente donde estabas.

    `/upgrade` sigue viva como ruta: el href real se mantiene en los items del sidebar, así
    que ctrl-click / abrir en pestaña nueva siguen funcionando y el deep link no se rompe.
  -->
  <teleport to="body">
    <transition name="upgrade-ov">
      <div
        v-if="open"
        class="upgrade-ov"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('upgrade.hero.title')"
        @click.self="close"
      >
        <div ref="panelEl" class="upgrade-ov--panel" tabindex="-1">
          <button
            type="button"
            class="upgrade-ov--close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <PhX :size="24" weight="regular" />
          </button>

          <!-- El scroll es de ESTE wrapper, no del panel: el panel está acotado a 85vh y
               el close vive fuera del área scrolleable, así que queda fijo en la esquina
               en vez de irse con el contenido. -->
          <div class="upgrade-ov--scroll">
            <!-- `.upgrade` aporta el fondo, el color base y la tipografía del upsell, igual
                 que en la página; lo único que se neutraliza es su `min-height: 100vh`, que
                 adentro de un panel con scroll propio no aplica. -->
            <div class="upgrade">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { PhX } from '@phosphor-icons/vue';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';

export default {
  name: 'UpgradeOverlay',
  components: { PhX },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const panelEl = ref(null);
    // El foco vuelve al item del sidebar que abrió la capa; sin esto queda en el <body> y
    // el siguiente Tab arranca desde el principio del documento.
    let lastFocused = null;

    const close = () => emit('close');

    // Mismo lock centralizado que usan los drawers y los modales (ref-count + compensación
    // del ancho de scrollbar), en vez de tocar `body.style.overflow` a mano.
    useBodyScrollLock(() => props.open);

    const onKeydown = (e) => {
      if (e.key === 'Escape' && props.open) close();
    };

    watch(() => props.open, async (isOpen) => {
      if (isOpen) {
        lastFocused = document.activeElement;
        await nextTick();
        // Al PANEL, no al botón de cerrar: el foco tiene que entrar al diálogo para que
        // el Tab siga adentro y el Esc funcione, pero enfocar el close le dibuja el anillo
        // encima del hero apenas abre, como si el usuario lo hubiera tabulado.
        panelEl.value?.focus();
      } else {
        lastFocused?.focus?.();
        lastFocused = null;
      }
    });

    onMounted(() => document.addEventListener('keydown', onKeydown));
    onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

    return { panelEl, close };
  },
};
</script>

<style scoped lang="scss">
// Scrim + panel. z-index en la banda de los diálogos (los drawers viven en 900/901 y el
// `ConfirmModal` en 10001): esta capa va sobre el sidebar, debajo de las confirmaciones.
.upgrade-ov {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 24px;
  box-sizing: border-box;
  // Se mantiene `auto` como red de seguridad para viewports muy bajos, donde
  // 85vh + los 80px de padding no entran. En el caso normal no scrollea nada
  // acá: el scroll es del panel.
  overflow-y: auto;
  overscroll-behavior: contain;
  // Scrim oscuro sin blur, igual que el de los drawers: la app se lee atrás pero
  // claramente en segundo plano.
  background: color-mix(in oklab, var(--slate) 72%, transparent);

  @media (max-width: 700px) { padding: 0; }
}

.upgrade-ov--panel {
  position: relative;
  width: 100%;
  max-width: 1240px;   // el mismo contenedor que usa la página
  // Alto acotado: el upsell se lee como MODAL, no como una página larga detrás de
  // un scrim. Antes el panel crecía con el contenido y el que scrolleaba era el
  // scrim, así que en pantallas chicas el modal medía más que el viewport y no se
  // percibía como una capa.
  max-height: 85vh;
  // `margin: auto` y no `align-items: center` en el scrim: centra igual, pero sin
  // el clipping del borde superior que da centrar un flex item que desborda.
  margin: auto;
  // Columna para que el wrapper de scroll pueda tomar el alto restante.
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-md);
  overflow: hidden;    // recorta el contenido al radius (el hero llega hasta el borde)
  outline: none;

  // En mobile sigue full-bleed: ahí el upsell ocupa la pantalla completa a
  // propósito (padding 0 en el scrim, sin radius), así que el tope de 85vh no aplica.
  @media (max-width: 700px) {
    border-radius: 0;
    min-height: 100%;
    max-height: none;
  }

  // El contenido trae el `min-height: 100vh` de la página: adentro del panel el alto lo
  // da el contenido y el scroll lo maneja el wrapper.
  .upgrade { min-height: 0; }
}

// El área scrolleable. `min-height: 0` es imprescindible: sin eso un flex item no
// baja de su alto de contenido y el overflow nunca se activa.
.upgrade-ov--scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

// El close flota sobre el hero (que es oscuro), así que va en vellum con una placa
// translúcida para sostenerse también si el scroll lo deja sobre el fondo claro.
.upgrade-ov--close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid color-mix(in oklab, var(--vellum) 30%, transparent);
  border-radius: var(--radius-sm);
  background: color-mix(in oklab, var(--slate) 55%, transparent);
  color: var(--vellum);
  cursor: pointer;
  transition: background var(--duration-base, 240ms) var(--ease-default, ease),
              border-color var(--duration-base, 240ms) var(--ease-default, ease);

  &:hover {
    background: var(--slate);
    border-color: color-mix(in oklab, var(--vellum) 60%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--vellum);
    outline-offset: 2px;
  }
}

// Entrada/salida: el scrim funde y el panel sube apenas. Sin escala — el DS es sober.
.upgrade-ov-enter-active,
.upgrade-ov-leave-active {
  transition: opacity var(--duration-base, 240ms) var(--ease-default, ease);

  .upgrade-ov--panel {
    transition: transform var(--duration-base, 240ms) var(--ease-default, ease);
  }
}
.upgrade-ov-enter-from,
.upgrade-ov-leave-to {
  opacity: 0;

  .upgrade-ov--panel { transform: translateY(12px); }
}

@media (prefers-reduced-motion: reduce) {
  .upgrade-ov-enter-active,
  .upgrade-ov-leave-active {
    transition: none;
    .upgrade-ov--panel { transition: none; }
  }
}
</style>
